/**
 * Categorises a receipt-detail exception. Type drives the email
 * template variant for vendor notifications, the icon shown in the
 * receipt-line UI, and the per-tenant `reasonCode` dropdown options.
 *
 * Stored as varchar (not MySQL enum) for extensibility — same as
 * `inventory_movement.type`.
 *
 * Type vs reasonCode: `type` is a fixed taxonomy used by code
 * (templates, dashboards, scorecards). `reasonCode` is a free-form
 * per-tenant configurable string (e.g. "Crushed packaging", "Wet
 * pallet", "Pallet count mismatch") that lets each tenant build
 * their own dropdowns without code changes.
 */
export enum PurchaseOrderReceiptExceptionType {
	/** Item arrived physically damaged. Quarantined, awaits vendor credit / RTV. */
	DAMAGED = 'DAMAGED',
	/** Vendor under-shipped versus packing slip. No inventory impact; metadata for credit chase. */
	SHORT = 'SHORT',
	/** Vendor over-shipped. Extra qty was kept (added to sellable stock) but flagged for audit. */
	OVER = 'OVER',
	/** Refused at the dock — never took possession. No inventory impact. */
	REFUSED = 'REFUSED',
	/** Wrong item shipped — refused. No inventory impact. */
	WRONG_ITEM = 'WRONG_ITEM',
	/** Item arrived past its expiration date. Quarantined. */
	EXPIRED = 'EXPIRED',
	/** Item arrived close enough to expiration to be unsellable. Quarantined. */
	NEAR_EXPIRY = 'NEAR_EXPIRY',
	/** Vendor invoiced/quoted price doesn't match what was on the PO. No inventory impact; pricing dispute. */
	PRICE_DISCREPANCY = 'PRICE_DISCREPANCY',
	/** Item arrived in unexpected packaging (case-pack mismatch, bulk vs eaches, etc.). Disposition varies. */
	WRONG_PACKAGING = 'WRONG_PACKAGING',
	/** Vendor delivered after committed date. No inventory impact; vendor performance signal. */
	LATE_DELIVERY = 'LATE_DELIVERY',
}

export type PurchaseOrderReceiptExceptionTypeValue =
	`${PurchaseOrderReceiptExceptionType}`;

export const PurchaseOrderReceiptExceptionTypeLabels: Record<
	PurchaseOrderReceiptExceptionType,
	string
> = {
	[PurchaseOrderReceiptExceptionType.DAMAGED]: 'Damaged on arrival',
	[PurchaseOrderReceiptExceptionType.SHORT]: 'Short-shipped by vendor',
	[PurchaseOrderReceiptExceptionType.OVER]: 'Over-shipped by vendor',
	[PurchaseOrderReceiptExceptionType.REFUSED]: 'Refused at dock',
	[PurchaseOrderReceiptExceptionType.WRONG_ITEM]: 'Wrong item shipped',
	[PurchaseOrderReceiptExceptionType.EXPIRED]: 'Expired on arrival',
	[PurchaseOrderReceiptExceptionType.NEAR_EXPIRY]: 'Too close to expiration',
	[PurchaseOrderReceiptExceptionType.PRICE_DISCREPANCY]: 'Price discrepancy',
	[PurchaseOrderReceiptExceptionType.WRONG_PACKAGING]: 'Wrong packaging',
	[PurchaseOrderReceiptExceptionType.LATE_DELIVERY]: 'Late delivery',
};
